import React, { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { TriangleAlert } from "lucide-react";

import CodeBlock from "./CodeBlock";
import FullView from "./FullView";
import { lazyChunk } from "./lazyChunk";

/* A ```mermaid block, drawn as its diagram in the code block's tray: Expand opens it in the
   app's viewer (`FullView`) and Copy copies its source. While the fence is still being written, while Mermaid's
   chunk loads (once, with the first diagram) and when the source doesn't parse, it is the
   source as code — the last with "Couldn't draw this diagram" on the strip and Mermaid's error
   in its tooltip — never a blank. A switch to dark or light draws it again, keeping the old
   drawing until the new one is ready.

   The SVG is Mermaid's own markup, drawn at its strict level (`mermaid.ts`). */

type Drawing = { source: string; svg?: string; error?: string };

const mermaid = lazyChunk(() => import("./mermaid"));

export default function Diagram({ source, writing }: { source: string; writing: boolean }) {
  const dark = useDarkMode();
  const [drawing, setDrawing] = useState<Drawing | null>(null);
  const [full, setFull] = useState(false);
  const frame = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (writing) return;
    let live = true;
    // the block's width when it is drawn (the card inside is a little narrower and shrinks the
    // chart by those pixels); ceiling: a later resize doesn't lay it out again
    mermaid
      .load()
      .then((module) => module.drawDiagram(source, dark, frame.current?.clientWidth ?? 0))
      .then(
        (svg) => live && setDrawing({ source, svg }),
        (error) => live && setDrawing({ source, error: error instanceof Error ? error.message : String(error) }),
      );
    return () => {
      live = false;
    };
  }, [source, dark, writing]);

  const drawn = !writing && drawing?.source === source ? drawing : null;
  if (!drawn?.svg) {
    const failed = drawn?.error && (
      <span className="inline-flex items-center gap-1.5" title={drawn.error}>
        <TriangleAlert className="w-3.5 h-3.5 shrink-0 text-[var(--warn)]" strokeWidth={1.75} />
        Couldn't draw this diagram
        <span className="sr-only">: {drawn.error}</span>
      </span>
    );
    return (
      <div ref={frame} className="min-w-0">
        <CodeBlock code={source} lang="mermaid" title={failed || undefined} />
      </div>
    );
  }
  const svg = { __html: drawn.svg };
  // full size fills the viewer, but no narrower than the diagram's own width (it scrolls instead)
  // and no taller than the viewer while there is room
  const [, width, height] = /viewBox="[-\d.]+ [-\d.]+ ([\d.]+) ([\d.]+)"/.exec(drawn.svg) ?? [];
  const fullWidth = width ? `max(${width}px, min(100%, calc((94dvh - 136px) * ${Number(width) / Number(height)})))` : "100%";
  return (
    <div ref={frame} className="min-w-0">
      <CodeBlock code={source} lang="mermaid" title="Diagram" onExpand={() => setFull(true)}>
        <div className="flex justify-center [&_svg]:h-auto [&_svg]:max-w-full" dangerouslySetInnerHTML={svg} />
      </CodeBlock>
      {full && (
        <FullView
          title="diagram.svg"
          kind="svg"
          about="Mermaid diagram"
          download={new Blob([drawn.svg], { type: "image/svg+xml" })}
          onClose={() => setFull(false)}
        >
          <div className="shrink-0 [&_svg]:!max-w-none [&_svg]:w-full [&_svg]:h-auto" style={{ width: fullWidth }} dangerouslySetInnerHTML={svg} />
        </FullView>
      )}
    </div>
  );
}

/** Whether the page is dark: `.dark` on <html>, in the platform and the extension alike. */
function useDarkMode(): boolean {
  return useSyncExternalStore(watchTheme, () => document.documentElement.classList.contains("dark"));
}

function watchTheme(onChange: () => void) {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
  return () => observer.disconnect();
}
